// Copyright (c) 2026 Aravind Adari. All rights reserved.

import { createTheme, PaletteMode } from "@mui/material/styles";

declare module "@mui/material/styles" {
  interface Palette {
    brand: { yellow: string; red: string; green: string; blue: string };
  }
  interface PaletteOptions {
    brand?: { yellow: string; red: string; green: string; blue: string };
  }
}

// Exact colors extracted from the StarOne logo
const LOGO = {
  green:  "#2E7D32",   // central figure + text (kept for brand palette)
  blue:   "#1565C0",   // right figure — now the PRIMARY color
  red:    "#C62828",   // left figure
  yellow: "#F9A825",   // crescent swoosh
};

// Primary = dark blue (replaces green throughout)
const PRIMARY = {
  main:  "#1565C0",
  light: "#1E88E5",
  dark:  "#0D47A1",
};

// Subtle tinted surfaces
const TINT = {
  blueBg:   "#E3F2FD",
  greenBg:  "#E8F5E9",
};

export function getTheme(mode: PaletteMode) {
  const isLight = mode === "light";
  return createTheme({
    palette: {
      mode,
      primary:   PRIMARY,
      secondary: { main: LOGO.green, light: "#4CAF50", dark: "#1B5E20" },
      error:     { main: LOGO.red },
      warning:   { main: LOGO.yellow, contrastText: "#5d3f00" },
      brand: {
        yellow: LOGO.yellow,
        red:    LOGO.red,
        green:  LOGO.green,
        blue:   LOGO.blue,
      },
      ...(isLight
        ? {
            background: { default: "#F4F6F9", paper: "#FFFFFF" },
            text: { primary: "#1A1A2E", secondary: "#5C6370" },
          }
        : {
            // VS Code Dark+ palette
            background: {
              default: "#1E1E1E",
              paper:   "#252526",
            },
            text: { primary: "#D4D4D4", secondary: "#9CDCFE" },
          }),
    },
    typography: {
      fontFamily: "'Inter', 'Roboto', 'Helvetica', 'Arial', sans-serif",
      fontSize: 14,
      h4: { fontWeight: 800, letterSpacing: "-0.5px" },
      h5: { fontWeight: 700 },
      h6: { fontWeight: 600 },
      subtitle1: { fontWeight: 600 },
    },
    shape: { borderRadius: 6 },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: "none" as const,
            fontWeight: 600,
            borderRadius: 8,
            padding: "8px 20px",
          },
          // Solid colors — no gradients
          containedPrimary: {
            backgroundColor: PRIMARY.main,
            boxShadow: "0 2px 6px rgba(21,101,192,0.30)",
            "&:hover": {
              backgroundColor: PRIMARY.dark,
              boxShadow: "0 3px 10px rgba(21,101,192,0.40)",
            },
          },
          containedSecondary: {
            backgroundColor: LOGO.green,
            boxShadow: "0 2px 6px rgba(46,125,50,0.25)",
            "&:hover": {
              backgroundColor: "#1B5E20",
              boxShadow: "0 3px 10px rgba(46,125,50,0.35)",
            },
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 10,
            boxShadow: isLight
              ? "0 2px 12px rgba(0,0,0,0.07)"
              : "0 2px 16px rgba(0,0,0,0.4)",
            border: isLight
              ? "1px solid rgba(0,0,0,0.06)"
              : "1px solid rgba(255,255,255,0.06)",
          },
        },
      },
      MuiPaper: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: {
            borderRadius: 10,
            border: isLight
              ? "1px solid rgba(0,0,0,0.07)"
              : "1px solid #3E3E42",
            boxShadow: isLight
              ? "0 1px 8px rgba(0,0,0,0.06)"
              : "0 2px 12px rgba(0,0,0,0.5)",
          },
        },
      },
      MuiDialog: { defaultProps: { PaperProps: { elevation: 3 } } },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: isLight ? "#FFFFFF" : "#2D2D30",
            borderBottom: isLight
              ? `2px solid ${PRIMARY.main}33`
              : `2px solid ${PRIMARY.dark}55`,
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundColor: isLight ? "#FFFFFF" : "#252526",
            backgroundImage: "none",
            borderRight: `1px solid ${isLight ? PRIMARY.main + "22" : "#3E3E42"}`,
          },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            margin: "2px 0",
            "&.Mui-selected": {
              backgroundColor: isLight
                ? `${PRIMARY.main}18`
                : `${PRIMARY.main}30`,
              backgroundImage: "none",
              borderLeft: `3px solid ${PRIMARY.main}`,
              color: isLight ? PRIMARY.dark : PRIMARY.light,
              "& .MuiListItemIcon-root": {
                color: isLight ? PRIMARY.dark : PRIMARY.light,
              },
              "&:hover": {
                backgroundColor: isLight
                  ? `${PRIMARY.main}28`
                  : `${PRIMARY.main}40`,
              },
            },
            "&:hover": {
              backgroundColor: isLight
                ? `${PRIMARY.main}0F`
                : "rgba(255,255,255,0.06)",
            },
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: "none",
            fontWeight: 600,
            "&.Mui-selected": {
              color: isLight ? PRIMARY.dark : PRIMARY.light,
            },
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          indicator: {
            backgroundColor: PRIMARY.main,
            height: 3,
            borderRadius: "3px 3px 0 0",
          },
        },
      },
      MuiInputBase: {
        styleOverrides: {
          input: {
            "&[type='time']::-webkit-calendar-picker-indicator": {
              filter: isLight ? "none" : "invert(1)",
            },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: PRIMARY.main,
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: PRIMARY.main,
              borderWidth: 2,
            },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: { fontWeight: 600, borderRadius: 6 },
        },
      },
      // Solid table header — no gradient
      MuiTableHead: {
        styleOverrides: {
          root: {
            "& .MuiTableCell-head": {
              backgroundColor: isLight ? PRIMARY.dark : "#2D2D30",
              color: isLight ? "#FFFFFF" : "#D4D4D4",
              fontWeight: 700,
              fontSize: "0.85rem",
              letterSpacing: "0.02em",
              borderBottom: isLight
                ? `2px solid ${LOGO.yellow}99`
                : "1px solid #3E3E42",
            },
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            "&:nth-of-type(even)": {
              backgroundColor: isLight
                ? TINT.blueBg
                : "rgba(255,255,255,0.03)",
            },
            "&:hover": {
              backgroundColor: isLight
                ? `${PRIMARY.main}12 !important`
                : "rgba(255,255,255,0.07) !important",
            },
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderBottom: isLight
              ? "1px solid rgba(0,0,0,0.06)"
              : "1px solid #3E3E42",
          },
        },
      },
      MuiLinearProgress: {
        styleOverrides: {
          root: {
            borderRadius: 4,
            backgroundColor: isLight
              ? `${PRIMARY.main}22`
              : `${PRIMARY.main}33`,
          },
          bar: {
            backgroundColor: PRIMARY.main,
            borderRadius: 4,
          },
        },
      },
      MuiAlert: {
        styleOverrides: {
          filledSuccess: { backgroundColor: LOGO.green },
          filledError:   { backgroundColor: LOGO.red },
          filledWarning: { backgroundColor: LOGO.yellow, color: "#3E2200" },
          filledInfo:    { backgroundColor: PRIMARY.main },
        },
      },
      MuiDivider: {
        styleOverrides: {
          root: {
            borderColor: isLight
              ? "rgba(0,0,0,0.08)"
              : "rgba(255,255,255,0.08)",
          },
        },
      },
    },
  });
}
